// src/components/admin/testSeries/MainsTestReviewList.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const MainsTestReviewList = () => {
  const [tests, setTests] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  useEffect(() => {
    fetchTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTests = async () => {
    try {
      setError("");
      const res = await axios.get(`${API_BASE}/api/admin/test-series/mains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTests(res.data);
      await fetchSubmissionCounts(res.data);
    } catch (err) {
      console.error("Error fetching Mains tests:", err);
      setError("Failed to load tests. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissionCounts = async (testList) => {
    const counts = {};
    for (const test of testList) {
      try {
        const res = await axios.get(
          `${API_BASE}/api/admin/test-series/mains/submissions/${test._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        counts[test._id] = res.data.length;
      } catch (err) {
        console.error(`Error fetching submissions for test ${test._id}:`, err);
        counts[test._id] = 0;
      }
    }
    setSubmissionCounts(counts);
  };

  const filteredTests = tests.filter((test) => {
    const matchesSearch =
      test.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (test.subject && test.subject.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSection = selectedSection === "" || test.section === selectedSection;
    return matchesSearch && matchesSection;
  });

  const uniqueSections = [...new Set(tests.map((test) => test.section).filter(Boolean))];

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          📋 Mains Test Submissions Review
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Select a test to review and evaluate student submissions
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-800 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded">
            {error}
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by test title or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            >
              <option value="">All Sections</option>
              {uniqueSections.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading tests...</p>
        </div>
      ) : filteredTests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {tests.length === 0
              ? "No Mains tests available yet."
              : "No tests match your search criteria."}
          </p>
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedSection("");
              }}
              className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTests.map((test) => (
            <div
              key={test._id}
              className="border border-gray-200 dark:border-gray-700 p-6 rounded-lg hover:shadow-md transition-shadow bg-white dark:bg-gray-800"
            >
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {test.title}
                  </h3>

                  {/* Section & Subject */}
                  <div className="flex flex-wrap gap-4 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-700 text-blue-800 dark:text-blue-200">
                      📚 {test.section || "No Section"}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-700 text-green-800 dark:text-green-200">
                      📖 {test.subject || "No Subject"}
                    </span>
                  </div>

                  {/* Test Info */}
                  <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <p>
                      <span className="font-medium">Date:</span> {formatDate(test.date)}
                    </p>
                    <p>
                      <span className="font-medium">Questions:</span> {test.questions?.length || 0}
                    </p>
                    <p>
                      <span className="font-medium">Uploaded by:</span>{" "}
                      {test.uploadedBy?.name || "—"}{" "}
                      {test.uploadedBy?.email && `(${test.uploadedBy.email})`}
                    </p>
                  </div>

                  {/* Submission Stats */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Submissions:</span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          submissionCounts[test._id] > 0
                            ? "bg-yellow-100 dark:bg-yellow-700 text-yellow-800 dark:text-yellow-200"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        }`}
                      >
                        {submissionCounts[test._id] !== undefined
                          ? `${submissionCounts[test._id]} submissions`
                          : "Loading..."}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="lg:ml-4">
                  <Link
                    to={`/admin/test-series/mains/submission/${test._id}`}
                    className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                      submissionCounts[test._id] > 0
                        ? "bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-800 text-white"
                        : "bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                    }`}
                    onClick={(e) => {
                      if (submissionCounts[test._id] === 0) {
                        e.preventDefault();
                        alert("No submissions available for this test yet.");
                      }
                    }}
                  >
                    📝 Review Submissions
                    {submissionCounts[test._id] > 0 && (
                      <span className="ml-2 bg-white bg-opacity-20 dark:bg-opacity-30 rounded-full px-2 py-1 text-xs">
                        {submissionCounts[test._id]}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {!loading && tests.length > 0 && (
        <div className="mt-8 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{tests.length}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Total Tests</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {Object.values(submissionCounts).reduce((a, b) => a + b, 0)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Total Submissions</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {Object.values(submissionCounts).filter((count) => count > 0).length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Tests with Submissions</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainsTestReviewList;
